import {
  Box,
  Button,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useRoom } from "../context/RoomContext";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 2;

const YES_NO_QUESTIONS = [
  { field: "heating24h", labelKey: "Heating24hLabel", heating24hfeeField: "heatingFee" },
  { field: "petPhotosVideos", labelKey: "PetPhotosVideosLabel" },
  { field: "customSnacks", labelKey: "CustomSnacksLabel" },
  { field: "ventilation24h", labelKey: "Ventilation24hLabel" },
  { field: "dailyDisinfection", labelKey: "DailyDisinfectionLabel" },
  { field: "dailyInteraction", labelKey: "DailyInteractionLabel" },
  { field: "customRequests", labelKey: "CustomRequestsLabel" },
  { field: "hasWindow", labelKey: "HasWindowLabel" },
  { field: "lateCheckout", labelKey: "LateCheckoutLabel", lateCheckoutfeeField: "lateCheckoutFee" },
];

const HotelAdd_RoomInfo_2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hotelId, roomId } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { updateRoomStep, fetchRoom, roomData, loading } = useRoom();
  const [searchParams] = useSearchParams();
  const manage = searchParams.get('manage') || '';


  const [formData, setFormData] = useState({
    heating24h: "",
    heatingFee: "",
    petPhotosVideos: "",
    customSnacks: "",
    ventilation24h: "",
    dailyDisinfection: "",
    dailyInteraction: "",
    customRequests: "",
    hasWindow: "",
    lateCheckout: "",
    lateCheckoutFee: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (roomId) {
      fetchRoom(roomId);
    }
  }, [roomId, fetchRoom]);


  useEffect(() => {
    if (roomData?.information2) {
      const amenitiesData = roomData.information2 || {};
      setFormData({
        heating24h: amenitiesData.heating24h !== undefined ? (amenitiesData.heating24h ? "yes" : "no") : "",
        heatingFee: amenitiesData.heatingFee?.toString() || "",
        petPhotosVideos: amenitiesData.petPhotosVideos !== undefined ? (amenitiesData.petPhotosVideos ? "yes" : "no") : "",
        customSnacks: amenitiesData.customSnacks !== undefined ? (amenitiesData.customSnacks ? "yes" : "no") : "",
        ventilation24h: amenitiesData.ventilation24h !== undefined ? (amenitiesData.ventilation24h ? "yes" : "no") : "",
        dailyDisinfection: amenitiesData.dailyDisinfection !== undefined ? (amenitiesData.dailyDisinfection ? "yes" : "no") : "",
        dailyInteraction: amenitiesData.dailyInteraction !== undefined ? (amenitiesData.dailyInteraction ? "yes" : "no") : "",
        customRequests: amenitiesData.customRequests !== undefined ? (amenitiesData.customRequests ? "yes" : "no") : "",
        hasWindow: amenitiesData.hasWindow !== undefined ? (amenitiesData.hasWindow ? "yes" : "no") : "",
        lateCheckout: amenitiesData.lateCheckout !== undefined ? (amenitiesData.lateCheckout ? "yes" : "no") : "",
        lateCheckoutFee: amenitiesData.lateCheckoutFee?.toString() || "",
      });
    }
  }, [roomData?.information2]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);

      const information2 = {};
      YES_NO_QUESTIONS.forEach((question) => {
        if (formData[question.field]) {
          information2[question.field] = formData[question.field] === "yes";
        }
        if (question.heating24hfeeField && formData[question.heating24hfeeField]) {
          information2[question.heating24hfeeField] = formData[question.heating24hfeeField];
        }
        if (question.lateCheckoutfeeField && formData[question.lateCheckoutfeeField]) {
          information2[question.lateCheckoutfeeField] = formData[question.lateCheckoutfeeField];
        }
      });

      await updateRoomStep(roomId, "information2", { information2 });

      if (manage) {
        navigate(`/hotel/${hotelId}/room-info-3/${roomId}?manage=true`);
      } else {
        navigate(`/hotel/${hotelId}/room-info-3/${roomId}`);
      }
    } catch (err) {
      console.error("Error saving amenities:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      setIsSaving(true);

      const information2 = {};
      YES_NO_QUESTIONS.forEach((question) => {
        if (formData[question.field]) {
          information2[question.field] = formData[question.field] === "yes";
        }
        if (question.heating24hfeeField && formData[question.heating24hfeeField]) {
          information2[question.heating24hfeeField] = formData[question.heating24hfeeField];
        }
        if (question.lateCheckoutfeeField && formData[question.lateCheckoutfeeField]) {
          information2[question.lateCheckoutfeeField] = formData[question.lateCheckoutfeeField];
        }
      });

      await updateRoomStep(roomId, "information2", { information2 });

      if (manage) {
        navigate(`/hotel/room-management/${hotelId}`);
      } else {
        navigate(`/hotel/onboarding`);
      }
    } catch (err) {
      console.error("Error saving amenities:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
  };

  const labelSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 };

  const isNextDisabled =
    YES_NO_QUESTIONS.some((question) => !formData[question.field]) ||
    (formData.heating24h === "yes" && !formData.heatingFee) ||
    (formData.lateCheckout === "yes" && !formData.lateCheckoutFee);

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto", gap: manage && 1.5, width: isMobile ? "100%" : "60%", }}>
          {manage ? (
            <>
              <Button
                onClick={() => navigate(`/hotel/${hotelId}/room-info-1/${roomId}?manage=true`)}
                sx={{
                  minWidth: "auto",
                  width: isMobile ? "36px" : "44px",
                  height: isMobile ? "36px" : "44px",
                  borderRadius: "50%",
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  padding: 0,
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                }}
              >
                <ArrowLeft size={isMobile ? 18 : 22} />
              </Button>
              <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
                {t('RoomManagementTitle')} {t('AddaNewRoomTitle')}
              </Typography>
            </>
          ) : (
            STEPS.map((step, index) => (
              <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: isMobile ? "20px" : "30px",
                      height: isMobile ? "20px" : "30px",
                      borderRadius: "50%",
                      backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                      border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                      boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: isMobile ? "10px" : "13px",
                      fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                      color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                      textAlign: "center",
                      whiteSpace: "wrap",
                      width: isMobile ? "50px" : "100px",
                    }}
                  >
                    {t(step.labelKey)}
                  </Typography>
                </Box>
                {index < STEPS.length - 1 && (
                  <Box
                    sx={{
                      height: "1px",
                      width: isMobile ? "30px" : "200px",
                      backgroundColor: "#D1D1D1",
                      mx: "auto",
                      mb: isMobile ? "18px" : "24px",
                    }}
                  />
                )}
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 4, display: "flex", alignItems: "center", gap: 1 }}>
            {t('RoomInfoStep2Title')}
            <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
              {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
            </Box>
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
              {YES_NO_QUESTIONS.map((question) => (
                <Box key={question.field}>
                  <Typography sx={labelSx}>{t(question.labelKey)} <span style={{ color: "red" }}> *</span></Typography>
                  <RadioGroup
                    row
                    value={formData[question.field]}
                    onChange={(e) => handleInputChange(question.field, e.target.value)}
                  >
                    <FormControlLabel
                      value="yes"
                      control={
                        <Radio
                          sx={{
                            color: "#D1D1D1",
                            "&.Mui-checked": { color: "#31BACD" },
                          }}
                        />
                      }
                      label={t('Yes')}
                      sx={{ color: "#000", fontSize: isMobile ? "14px" : "16px" }}
                    />
                    <FormControlLabel
                      value="no"
                      control={
                        <Radio
                          sx={{
                            color: "#D1D1D1",
                            "&.Mui-checked": { color: "#31BACD" },
                          }}
                        />
                      }
                      label={t('No')}
                      sx={{ color: "#000", fontSize: isMobile ? "14px" : "16px" }}
                    />
                  </RadioGroup>

                  {question.heating24hfeeField && formData[question.field] === "yes" && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>
                        {t('AdditionalFeeLabel')}
                      </Typography>
                      <TextField
                        fullWidth
                        type="string"
                        placeholder={t('AdditionalFeePlaceHolder')}
                        value={formData[question.heating24hfeeField]}
                        onChange={(e) => handleInputChange(question.heating24hfeeField, e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                  )}
                  {question.lateCheckoutfeeField && formData[question.field] === "yes" && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontWeight: "400", fontSize: isMobile ? "14px" : "16px", color: "#7d7d7d", mb: 1 }}>
                        {t('AdditionalFeeLabel')}
                      </Typography>
                      <TextField
                        fullWidth
                        type="string"
                        placeholder={t('AdditionalFeePlaceHolder')}
                        value={formData[question.lateCheckoutfeeField]}
                        onChange={(e) => handleInputChange(question.lateCheckoutfeeField, e.target.value)}
                        sx={textFieldSx}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(manage ? `/hotel/${hotelId}/room-info-1/${roomId}?manage=true` : `/hotel/${hotelId}/room-info-1/${roomId}`)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_RoomInfo_2;
